import React, { useEffect, useMemo, useState, useContext, useRef } from "react";
import { MaterialReactTable } from "material-react-table";
import {
  Box,
  Paper,
  IconButton,
  Typography,
  TextField,
  InputAdornment,
  Stack,
  Divider,
  Button,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { URL as BASE_URL } from "../config";
import { LoaderContext } from "../Components/dashboard";

const themeColor = "#ff002b";

const PackageExpiredUserList = () => {
  const { setLoading } = useContext(LoaderContext);
  const [dataSource, setDataSource] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const navigate = useNavigate();
  const firstLoadRef = useRef(true); 

  const fetchExpiredUsers = async () => {
    try {
      if (firstLoadRef.current) setLoading(true);

      const res = await axios.get(`${BASE_URL}/userregistration/expired`);
      setDataSource(res.data || []);
    } catch (err) {
      console.error("Fetch expired users failed", err);
    } finally {
      if (firstLoadRef.current) {
        setLoading(false);
        firstLoadRef.current = false;
      }
    }
  };

  useEffect(() => {
    fetchExpiredUsers(); 

    const interval = setInterval(() => {
      fetchExpiredUsers(); 
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const handleEdit = (id) => {
    navigate(`/dashboard/userregistration/${id}`);
  };


  const columns = useMemo(
    () => [
      {
        accessorKey: "actions",
        header: "Action",
        size: 35,
        minSize: 35,
        maxSize: 40,
        Cell: ({ row }) => (
          <IconButton
            size="small"
            onClick={() => handleEdit(row.original.userId)}
            sx={{
              color: themeColor,
              backgroundColor: "#ffe6e9",
              "&:hover": { backgroundColor: "#ffe6e9" },
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        ),
      },
      { accessorKey: "userId", header: "User ID", size: 50 },
      { accessorKey: "userName", header: "User Name", size: 150 },
      { accessorKey: "mobileNo", header: "Mobile No", size: 120 },
      { accessorKey: "email", header: "Email", size: 180 },
      {
        accessorKey: "packageEnddate",
        header: "Expiry Date",
        size: 120,
        Cell: ({ cell }) => {
          const dateStr = cell.getValue()?.toString();
          if (!dateStr) return "-";

          const d = new Date(dateStr);
          if (isNaN(d)) return "-";

          const day = String(d.getDate()).padStart(2, "0");
          const month = String(d.getMonth() + 1).padStart(2, "0");
          const year = d.getFullYear();

          return `${day}/${month}/${year}`;
        },
      },
      {
        accessorKey: "whatsapp",
        header: "Send WhatsApp",
        size: 150,
        Cell: ({ row }) => {
          const mobile = row.original.mobileNo;
          const userName = row.original.userName;

          if (!mobile) return "-";

          const sendNotification = async () => {
            const button = document.getElementById(`whatsapp-btn-${row.original.id}`);
            try {
              if (button) {
                button.innerHTML = "Sending...";
                button.disabled = true;
                button.style.opacity = "0.7";
              }

              const formData = new FormData();
              formData.append("title", "expired");
              formData.append("number", mobile.toString());

              const res = await axios.post(`${BASE_URL}/send-whatsapp`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
              });

              if (res.data?.success) {
                alert(`WhatsApp Message sent successfully to ${userName}`);
                if (button) {
                  button.innerHTML = "✓ Sent";
                  button.style.background = "linear-gradient(135deg, #4CAF50, #45a049)";
                  button.style.opacity = "1";
                }
              } else {
                alert("Failed to send WhatsApp Message");
                if (button) {
                  button.innerHTML = "WhatsApp";
                  button.disabled = false;
                  button.style.opacity = "1";
                }
              }
            } catch (error) {
              console.error("WhatsApp error:", error);
              alert("Failed to send WhatsApp Message");
              if (button) {
                button.innerHTML = "WhatsApp";
                button.disabled = false;
                button.style.opacity = "1";
              }
            }
          };

          return (
            <Button
              id={`whatsapp-btn-${row.original.id}`}
              size="small"
              variant="contained"
              startIcon={
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
                  alt="WhatsApp"
                  width={18}
                  height={18}
                />
              }
              sx={{
                background: "linear-gradient(135deg, #25D366, #128C7E)",
                color: "#fff",
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "12px",
                px: 2,
                py: 0.5,
                fontSize: "12px",
                boxShadow: "0 4px 10px rgba(0,128,0,0.3)",
                minWidth: "110px",
                "&:hover": {
                  background: "linear-gradient(135deg, #128C7E, #25D366)",
                  boxShadow: "0 6px 14px rgba(0,128,0,0.45)",
                },
              }}
              onClick={sendNotification}
            >
              WhatsApp
            </Button>
          );
        },
      },
    ],
    []
  );

  const filteredData = useMemo(() => {
    return dataSource.filter((item) =>
      Object.values(item)
        .join(" ")
        .toLowerCase()
        .includes(globalFilter.toLowerCase())
    );
  }, [dataSource, globalFilter]);

  return (
    <Box sx={{ p: { xs: 0.1, md: 1 } }}>
      <Paper elevation={4} sx={{ p: { xs: 1, md: 2 }, borderRadius: 3 }}>
       
        <Box sx={{ display: "flex", justifyContent: "center", mt: 1.5, mb: 1.8 }}>
          <Box
            sx={{
              background: `linear-gradient(90deg, ${themeColor}, #ff6a6a)`,
              px: { xs: 2.5, md: 4 },
              py: { xs: 0.8, md: 1.1 },
              borderRadius: "8px 22px 8px 22px",
              boxShadow: "0 6px 16px rgba(255,92,110,0.35)",
            }}
          >
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: 900,
                color: "#fff",
                letterSpacing: "0.7px",
                textTransform: "uppercase",
              }}
            >
              Expired Users
            </Typography>
          </Box>
        </Box>

        <Divider
          sx={{
            mb: 2.2,
            background: `linear-gradient(90deg, transparent, ${themeColor}, transparent)`,
            height: "2px",
            borderRadius: 5,
          }}
        />

        <Stack alignItems="flex-end" sx={{ mb: 2.5 }}>
          <TextField
            size="small"
            placeholder="Search users..."
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: themeColor, fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: "100%", sm: 240 },
              "& .MuiOutlinedInput-root": {
                borderRadius: 20,
                height: 34,
                "& fieldset": { borderColor: "#454444" },
                "&:hover fieldset": {
                  borderColor: themeColor,
                  boxShadow: `0 0 0 2px ${themeColor}22`,
                },
                "&.Mui-focused fieldset": {
                  borderColor: themeColor,
                  boxShadow: `0 0 0 3px ${themeColor}33`,
                },
              },
            }}
          />
        </Stack>

        <MaterialReactTable
          columns={columns}
          data={filteredData}
          enableTopToolbar={false}
          enableColumnActions={false}
          enableDensityToggle={false}
          enableFullScreenToggle={false}
          enableColumnFilters={false}
          enableGlobalFilter={false}
          enableHiding={false}
          muiTablePaperProps={{ sx: { boxShadow: "none", mt: 0.5 } }}
          muiTableContainerProps={{
            sx: {
              maxHeight: { xs: "55vh", sm: "none" },
              "&::-webkit-scrollbar": { height: "6px" },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": { backgroundColor: "#cfcfcf", borderRadius: "4px" },
              "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#b0b0b0" },
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              backgroundColor: "#ffe6e9",
              fontWeight: 700,
              fontSize: { xs: "12px", sm: "14px" },
              py: { xs: 0.8, sm: 1.2 },
              whiteSpace: "nowrap",
            },
          }}
          muiTableBodyRowProps={{
            sx: {
              "& td": { fontSize: { xs: "11px", sm: "14px" }, py: { xs: 0.5, sm: 1 }, whiteSpace: "nowrap" },
              "&:hover": { backgroundColor: "#fff8e1" },
            },
          }}
          initialState={{ pagination: { pageSize: 50, pageIndex: 0 } }}
        />
      </Paper>
    </Box>
  );
};

export default PackageExpiredUserList;
